/**
 * Хук для работы с ресторанами
 */

import { useState, useEffect } from 'react';
import restaurantService from '../services/restaurantService';
import { Restaurant } from '../types/api';
import userService from '../services/userService';

interface UseRestaurantsProps {
  telegramId?: string | null;
  autoLoadLastRestaurant?: boolean;
}

export const useRestaurants = (props?: UseRestaurantsProps) => {
  const { telegramId, autoLoadLastRestaurant = true } = props || {};
  
  const [restaurants, setRestaurants] = useState<Restaurant[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedRestaurantId, setSelectedRestaurantId] = useState<string | null>(null);
  const [isRestoringLastRestaurant, setIsRestoringLastRestaurant] = useState(false);

  /**
   * Загружает список ресторанов из базы данных
   */
  const fetchRestaurantSuggestions = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const data = await restaurantService.getRestaurants();
      setRestaurants(data.restaurants);
      
      return { restaurants: data.restaurants };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Ошибка загрузки ресторанов';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Фильтрует предложения по поисковому запросу
   */
  const filterSuggestions = (query: string): string[] => {
    if (!query.trim()) {
      return restaurants.map((r: Restaurant) => r.name);
    }
    
    const lowerQuery = query.toLowerCase();
    return restaurants.filter((restaurant: Restaurant) => 
      restaurant.name.toLowerCase().includes(lowerQuery)
    ).map((r: Restaurant) => r.name);
  };

  /**
   * Очищает ошибку
   */
  const clearError = () => {
    setError(null);
  };

  // Загружаем предложения при инициализации хука
  useEffect(() => {
    fetchRestaurantSuggestions();
  }, []);

  // Загружаем последний выбранный ресторан (приоритет: БД -> localStorage)
  useEffect(() => {
    const loadLastRestaurant = async () => {
      console.log('[useRestaurants] Попытка загрузки последнего ресторана:', {
        telegramId,
        autoLoadLastRestaurant,
        restaurantsCount: restaurants.length,
        selectedRestaurantId
      });
      
      if (!autoLoadLastRestaurant || restaurants.length === 0) {
        console.log('[useRestaurants] Пропуск восстановления: условие не выполнено');
        return;
      }

      // Пропускаем, если ресторан уже выбран
      if (selectedRestaurantId) {
        console.log('[useRestaurants] Пропуск восстановления: ресторан уже выбран');
        return;
      }

      setIsRestoringLastRestaurant(true);
      try {
        let lastRestaurantId: string | null = null;

        // 1. Сначала пытаемся загрузить из БД (если есть telegram_id)
        if (telegramId) {
          console.log('[useRestaurants] Запрос к БД для telegram_id:', telegramId);
          lastRestaurantId = await userService.getLastSelectedRestaurant(telegramId);
          console.log('[useRestaurants] Из БД:', lastRestaurantId);
        }

        // 2. Если в БД ничего нет, пробуем localStorage
        if (!lastRestaurantId) {
          lastRestaurantId = localStorage.getItem('selectedRestaurantId');
          console.log('[useRestaurants] Из localStorage:', lastRestaurantId);
        }
        
        if (lastRestaurantId) {
          // Проверяем, что ресторан существует в списке
          const restaurantExists = restaurants.some((r: Restaurant) => r.restaurant_id === lastRestaurantId);
          
          if (restaurantExists) {
            setSelectedRestaurantId(lastRestaurantId);
            // Синхронизируем localStorage и БД
            localStorage.setItem('selectedRestaurantId', lastRestaurantId);
            console.log('[useRestaurants] ✅ Восстановлен ресторан:', lastRestaurantId);
          } else {
            console.warn('[useRestaurants] ❌ Ресторан не найден в списке:', lastRestaurantId);
            // Очищаем некорректные значения
            localStorage.removeItem('selectedRestaurantId');
          }
        } else {
          console.log('[useRestaurants] Нет сохраненного ресторана');
        }
      } catch (error) {
        console.error('[useRestaurants] ❌ Ошибка загрузки:', error);
      } finally {
        setIsRestoringLastRestaurant(false);
      }
    };

    loadLastRestaurant();
  }, [telegramId, autoLoadLastRestaurant, restaurants, selectedRestaurantId]);

  // Функция для обновления выбранного ресторана с сохранением в localStorage И БД
  const updateSelectedRestaurant = async (restaurantId: string | null) => {
    console.log('[useRestaurants] Обновление выбранного ресторана:', restaurantId);
    setSelectedRestaurantId(restaurantId);
    
    if (restaurantId) {
      // 1. Сохраняем в localStorage (для синхронизации между страницами)
      localStorage.setItem('selectedRestaurantId', restaurantId);
      console.log('[useRestaurants] ✅ Сохранено в localStorage:', restaurantId);

      // 2. Сохраняем в БД (для сохранения после выхода)
      if (telegramId) {
        try {
          await userService.updateLastSelectedRestaurant(telegramId, restaurantId);
          console.log('[useRestaurants] ✅ Сохранено в БД:', restaurantId);
        } catch (error) {
          console.error('[useRestaurants] ❌ Ошибка сохранения в БД:', error);
        }
      } else {
        console.log('[useRestaurants] Пропуск сохранения в БД (нет telegram_id)');
      }
    } else {
      localStorage.removeItem('selectedRestaurantId');
      console.log('[useRestaurants] Удалено из localStorage');
    }
  };

  return {
    suggestions: restaurants.map((r: Restaurant) => r.name),
    loading,
    error,
    fetchRestaurantSuggestions,
    filterSuggestions,
    clearError,
    restaurants,
    selectedRestaurantId,
    setSelectedRestaurantId: updateSelectedRestaurant,
    isRestoringLastRestaurant,
  };
};
